import { createSlice } from "@reduxjs/toolkit";


const initialState = {

    /*
     * ============================================================
     * NOTIFICATIONS
     * ============================================================
     */

    loading: false,

    notifications: [],

    unreadCount: 0,

    error: null,


    /*
     * ============================================================
     * CONVERSATION CACHE
     * ============================================================
     *
     * Chaque inquiryId possède éventuellement :
     *
     * {
     *     conversation,
     *     messages,
     *     loadedAt
     * }
     *
     * Exemple :
     *
     * conversationCache: {
     *
     *     "uuid-inquiry-1": {
     *         conversation: {...},
     *         messages: [...],
     *         loadedAt: 1755260000000
     *     }
     *
     * }
     */

    conversationCache: {},


    /*
     * ============================================================
     * CONVERSATION COURANTE
     * ============================================================
     */

    loadingConversation: false,

    selectedNotification: null,

    selectedConversation: null,

    inquiryId: null,

    messages: [],

    offer: null,


    /*
     * ============================================================
     * STATISTIQUES DE LA CONVERSATION COURANTE
     * ============================================================
     */

    buyerMessagesCount: 0,

    sellerMessagesCount: 0,

    receivedMessageCount: 0,

    canReply: true,


    /*
     * ============================================================
     * DRAWER
     * ============================================================
     */

    drawerOpen: false

};


const calculateMessageStatistics = (
    messages
) => {

    const safeMessages =
        Array.isArray(messages)
            ? messages
            : [];


    const buyerMessagesCount =
        safeMessages.filter(
            message =>
                message.senderType === "BUYER"
        ).length;


    const sellerMessagesCount =
        safeMessages.filter(
            message =>
                message.senderType === "SELLER"
        ).length;


    const receivedMessageCount =
        safeMessages.filter(
            message =>
                message.senderType !== "SYSTEM"
        ).length;


    return {

        buyerMessagesCount,

        sellerMessagesCount,

        receivedMessageCount

    };

};


const pendingConversationSlice = createSlice({

    name: "conversationNotification",

    initialState,

    reducers: {


        /*
         * ========================================================
         * NOTIFICATIONS
         * ========================================================
         */

        loadNotificationsStart(
            state
        ) {

            state.loading = true;

            state.error = null;

        },


        loadNotificationsSuccess(
            state,
            action
        ) {

            const notifications =
                Array.isArray(action.payload)
                    ? action.payload
                    : [];


            state.loading = false;

            state.notifications =
                notifications;


            state.unreadCount =
                notifications.filter(
                    notification =>
                        !notification.read
                ).length;

        },


        loadNotificationsFailure(
            state,
            action
        ) {

            state.loading = false;

            state.error =
                action.payload;

        },


        /*
         * ========================================================
         * MARK NOTIFICATION AS READ
         * ========================================================
         */

        markNotificationAsRead(
            state,
            action
        ) {

            const notificationId =
                action.payload;


            state.notifications =
                state.notifications.map(
                    notification =>

                        notification.id ===
                        notificationId

                            ? {
                                ...notification,
                                read: true
                            }

                            : notification
                );


            state.unreadCount =
                state.notifications.filter(
                    notification =>
                        !notification.read
                ).length;

        },


        /*
         * ========================================================
         * CONVERSATION LOADING
         * ========================================================
         */

        loadConversationStart(
            state
        ) {

            state.loadingConversation =
                true;

            state.error = null;

        },


        loadConversationSuccess(
            state,
            action
        ) {

            const {

                notification,

                conversation,

                messages,

                inquiryId

            } = action.payload;


            const safeMessages =
                Array.isArray(messages)
                    ? messages
                    : [];


            /*
             * Conversation courante
             */

            state.loadingConversation =
                false;


            state.selectedNotification =
                notification;


            state.selectedConversation =
                conversation;


            state.inquiryId =
                inquiryId;


            state.messages =
                safeMessages;

            state.offer = action.payload.offer ?? null;

            /*
             * Statistiques
             */

            const statistics =
                calculateMessageStatistics(
                    safeMessages
                );


            state.buyerMessagesCount =
                statistics.buyerMessagesCount;


            state.sellerMessagesCount =
                statistics.sellerMessagesCount;


            state.receivedMessageCount =
                statistics.receivedMessageCount;


            /*
             * Pour l'instant la possibilité
             * de répondre dépend du statut
             * de la conversation.
             *
             * Adapter les valeurs selon ton enum
             * backend.
             */

            state.canReply =
                conversation?.status === "OPEN";


            /*
             * Cache local
             */

            state.conversationCache[
                inquiryId
                ] = {

                conversation,

                messages: safeMessages,

                loadedAt: Date.now()

            };

        },


        loadConversationFailure(
            state,
            action
        ) {

            state.loadingConversation =
                false;

            state.error =
                action.payload;

        },


        /*
         * ========================================================
         * APPEND MESSAGE
         * ========================================================
         */

        appendMessage(
            state,
            action
        ) {

            const message =
                action.payload;


            state.messages.push(
                message
            );


            /*
             * Mise à jour du cache
             */

            if (
                state.inquiryId
            ) {

                const cached =
                    state.conversationCache[
                        state.inquiryId
                        ];


                if (cached) {

                    cached.messages.push(
                        message
                    );

                    cached.loadedAt =
                        Date.now();

                }

            }


            /*
             * Statistiques
             */

            if (
                message.senderType === "BUYER"
            ) {

                state.buyerMessagesCount++;

            }


            if (
                message.senderType === "SELLER"
            ) {

                state.sellerMessagesCount++;

            }


            if (
                message.senderType !== "SYSTEM"
            ) {

                state.receivedMessageCount++;

            }

        },


        /*
         * ========================================================
         * DRAWER
         * ========================================================
         */

        openDrawer(
            state
        ) {

            state.drawerOpen =
                true;

        },


        closeDrawer(
            state
        ) {

            state.drawerOpen =
                false;

        },


        /*
         * ========================================================
         * CLEAR CURRENT CONVERSATION
         * ========================================================
         *
         * On ferme le drawer mais on conserve le cache.
         *
         * C'est important :
         *
         * fermeture du drawer
         *       ≠
         * suppression des données
         */

        clearCurrentConversation(
            state
        ) {

            state.selectedConversation =
                null;

            state.selectedNotification =
                null;

            state.inquiryId =
                null;

            state.messages =
                [];

            state.buyerMessagesCount =
                0;

            state.sellerMessagesCount =
                0;

            state.receivedMessageCount =
                0;

            state.canReply =
                true;

            state.drawerOpen =
                false;

        },


        /*
         * ========================================================
         * INVALIDATE CONVERSATION CACHE
         * ========================================================
         */

        invalidateConversationCache(
            state,
            action
        ) {

            const inquiryId =
                action.payload;


            delete state.conversationCache[
                inquiryId
                ];

        },


        /*
         * ========================================================
         * CLEAR ENTIRE CACHE
         * ========================================================
         */

        clearConversationCache(
            state
        ) {

            state.conversationCache =
                {};

        }

    }

});


export const {

    loadNotificationsStart,

    loadNotificationsSuccess,

    loadNotificationsFailure,

    loadConversationStart,

    loadConversationSuccess,

    loadConversationFailure,

    appendMessage,

    openDrawer,

    closeDrawer,

    markNotificationAsRead,

    clearCurrentConversation,

    invalidateConversationCache,

    clearConversationCache

} = pendingConversationSlice.actions;


export default pendingConversationSlice.reducer;